import axios, { AxiosResponse } from "axios";
import authStore from "~/stores/auth";
import qs from "qs";
import { ReqStatus } from "~/util";

// Full request/response context attached to every API error so the UI can show it.
export interface ApiErrorContext {
  method?: string;
  url?: string;
  status?: number;
  statusText?: string;
  responseHeaders?: unknown;
  responseData?: unknown;
}

const contextFromResponse = (response: any, config?: any): ApiErrorContext => {
  const cfg = config ?? response?.config;
  return {
    method: cfg?.method?.toUpperCase(),
    url: cfg?.url,
    status: response?.status,
    statusText: response?.statusText,
    responseHeaders: response?.headers,
    responseData: response?.data,
  };
};

const contextFromAxios = (err: any): ApiErrorContext =>
  contextFromResponse(err?.response, err?.config ?? err?.response?.config);

// Build a full, copyable dump of the response: request line, status, headers and body.
const fullResponseString = (ctx: ApiErrorContext): string | undefined => {
  const payload: Record<string, unknown> = {};
  const request = [ctx.method, ctx.url].filter(Boolean).join(" ");
  if (request) payload.request = request;
  if (ctx.status !== undefined) {
    payload.status = ctx.statusText ? `${ctx.status} ${ctx.statusText}` : ctx.status;
  }
  if (ctx.responseHeaders !== undefined) payload.headers = ctx.responseHeaders;
  if (ctx.responseData !== undefined) payload.body = ctx.responseData;
  if (Object.keys(payload).length === 0) return undefined;
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return undefined;
  }
};

// Endpoint-specific errors come back as a list, either of plain strings
// (["Access of linkable documents must be the same"]) or of objects
// ([{ error: "...", code: 4102 }]). Both carry a message meant for the user.
const apiErrorMessages = (data: unknown): string[] => {
  if (!Array.isArray(data)) return [];
  return data
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object" && typeof (item as any).error === "string") return (item as any).error;
      return null;
    })
    .filter((message): message is string => !!message);
};

// Extract the full response dump from any thrown value (our errors or a raw axios error).
export const apiErrorDetails = (err: unknown): string | undefined => {
  if (err && typeof err === "object") {
    const ctx = (err as any).context as ApiErrorContext | undefined;
    if (ctx) return fullResponseString(ctx);
    const anyErr = err as any;
    if (anyErr.response || anyErr.isAxiosError) return fullResponseString(contextFromAxios(err));
    if (err instanceof Error && err.message) return err.message;
  }
  return err != null ? String(err) : undefined;
};

// Details of the most recent failed request that resolved to `null` (no throw), so callers
// that only see a `null` result (e.g. the upload page) can still surface the server response.
let lastApiErrorDetails: string | undefined;
export const consumeLastApiErrorDetails = (): string | undefined => {
  const details = lastApiErrorDetails;
  lastApiErrorDetails = undefined;
  return details;
};

class NetworkError extends Error {
  context?: ApiErrorContext;

  constructor(cause?: unknown) {
    super("Network error - no connection or the server is unreachable");
    this.name = "NetworkError";
    if (cause !== undefined) {
      (this as any).cause = cause;
      this.context = contextFromAxios(cause);
    }
    // target is es5 - keep instanceof working for Error subclasses
    Object.setPrototypeOf(this, NetworkError.prototype);
  }

  toString() {
    return "NetworkError";
  }
}

export class NotFound extends Error {
  context?: ApiErrorContext;

  constructor(cause?: unknown) {
    super("Not found");
    this.name = "NotFound";
    if (cause !== undefined) {
      (this as any).cause = cause;
      this.context = contextFromAxios(cause);
    }
    Object.setPrototypeOf(this, NotFound.prototype);
  }

  toString() {
    return "Not found";
  }
}

export class APIError extends Error {
  context?: ApiErrorContext;

  constructor(response: AxiosResponse) {
    super(apiErrorMessages(response.data).join(", ") || "API error");
    this.name = "APIError";
    this.context = contextFromResponse(response);
    Object.setPrototypeOf(this, APIError.prototype);
  }

  toString() {
    return this.message;
  }
}

export class FieldValidationError extends Error {
  fieldErrors: Record<string, string[]>;
  context?: ApiErrorContext;

  constructor(fieldErrors: Record<string, string[]>, context?: ApiErrorContext) {
    super(
      Object.entries(fieldErrors)
        .map(([k, v]) => `${k}: ${v.join(", ")}`)
        .join("; ") || "Field validation error"
    );
    this.name = "FieldValidationError";
    this.fieldErrors = fieldErrors;
    this.context = context;
    Object.setPrototypeOf(this, FieldValidationError.prototype);
  }

  toString() {
    return Object.entries(this.fieldErrors)
      .map(([k, v]) => `${k}: ${v.join(", ")}`)
      .join("; ");
  }
}

export const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT;
export const API_ROOT = `${import.meta.env.VITE_API_ENDPOINT}/api`;
export const APPLICATION_TOKEN = import.meta.env.VITE_APPLICATION_TOKEN;

export type QueryParams = Record<string, string | number | number[] | string[] | (number | string)[]>;

export const constructHeaders = async () => {
  const token = await authStore.getAuthToken();
  const refreshToken = await authStore.getRefreshToken();
  if (token === null) return null;
  const headers = {
    "X-Requested-With": "XMLHttpRequest",
    Authorization: `Bearer ${token}`,
  };
  return headers;
};

const performApiReq = async (cb: (headers: any) => any): Promise<AxiosResponse<any> | null> => {
  lastApiErrorDetails = undefined;
  try {
    const headers = await constructHeaders();
    if (headers === null) return null;
    const resp = await cb(headers);
    return resp;
  } catch (err: any) {
    // Preserve the full response so callers that only see a `null` result can still show it.
    lastApiErrorDetails = fullResponseString(contextFromAxios(err));
    if (err.response === undefined) {
      // NetworkError (no connection or server is down)
      throw new NetworkError(err);
    }
    // Auth error
    if (
      err.response.status === 401 ||
      (typeof err.response.data === "string" && err.response.data?.includes("401 Client Error"))
    ) {
      authStore.logOut();
      return null;
    }
    // not found
    if (err.response.status === 404) {
      throw new NotFound(err);
    }
    if (apiErrorMessages(err.response.data).length !== 0) {
      // Specific error
      throw new APIError(err.response);
    }
    if (err.response.status === 403) {
      return null;
    }
    if (
      err.response.status === 400 &&
      err.response.data &&
      typeof err.response.data === "object" &&
      !Array.isArray(err.response.data)
    ) {
      const data = err.response.data as Record<string, unknown>;
      const fieldErrors: Record<string, string[]> = {};
      for (const [k, v] of Object.entries(data)) {
        if (Array.isArray(v) && v.every((item) => typeof item === "string")) {
          fieldErrors[k] = v as string[];
        }
      }
      if (Object.keys(fieldErrors).length > 0) {
        throw new FieldValidationError(fieldErrors, contextFromAxios(err));
      }
    }

    return null;
  }
};

const getPath = (path: string) => {
  return `${API_ROOT}/${path}/`;
};

// TODO: Add generic parameter to specify the data return type

export const del = async (path: string, data?: any) => {
  return await performApiReq((headers) =>
    axios.delete(getPath(path), {
      headers: headers,
      ...(data ? { data } : {}),
    })
  );
};

export async function getBare<T>(path: string, queryParams: QueryParams = {}) {
  return await performApiReq((headers) => {
    return axios.get<T>(path, {
      headers: headers,
      params: queryParams,
      paramsSerializer: (params) => {
        return qs.stringify(params, { arrayFormat: "comma" });
      },
    });
  });
}

export const get: typeof getBare = async (path: string, ...args) => {
  return await getBare(getPath(path), ...args);
};

export const post = async (
  path: string,
  body: Record<string, any> = {},
  additionalOptions: Record<string, any> = {}
) => {
  return await performApiReq((headers) => {
    return axios.post(getPath(path), body, {
      headers,
      ...additionalOptions,
    });
  });
};

export const put = async (
  path: string,
  body: Record<string, any> = {},
  additionalOptions: Record<string, any> = {}
) => {
  return await performApiReq((headers) =>
    axios.put(getPath(path), body, {
      headers,
      ...additionalOptions,
    })
  );
};

export const patch = async (
  path: string,
  body: Record<string, any> = {},
  additionalOptions: Record<string, any> = {}
) => {
  return await performApiReq((headers) =>
    axios.patch(getPath(path), body, {
      headers,
      ...additionalOptions,
    })
  );
};

export const postMultipart = async (path: string, body: FormData) => {
  return await performApiReq((headers) => {
    delete headers["x-application-token"];
    return axios.post(getPath(path), body, {
      headers: { ...headers, "content-type": "multipart/form-data" },
    });
  });
};

export { ReqStatus };
